import { useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { toast } from "sonner";
import { Loader2, Sparkles, Copy, Check, Linkedin, Twitter, Instagram, Facebook, Send, Mail, FileText, Rocket, MessageSquare, ClipboardList } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { aiMarketing } from "@/lib/ai";
import { fetchCampaigns } from "@/lib/api";
import { useEffect } from "react";

const CHANNELS = [
  { id: "linkedin",       label: "LinkedIn",       icon: Linkedin },
  { id: "x",              label: "X / Twitter",    icon: Twitter },
  { id: "instagram",      label: "Instagram",      icon: Instagram },
  { id: "facebook",       label: "Facebook",       icon: Facebook },
  { id: "reddit",         label: "Reddit",         icon: MessageSquare },
  { id: "producthunt",    label: "Product Hunt",   icon: Rocket },
  { id: "email",          label: "Email",          icon: Mail },
  { id: "press_release",  label: "Press release",  icon: FileText },
  { id: "discord",        label: "Discord",        icon: Send },
  { id: "launch_checklist", label: "Launch checklist", icon: ClipboardList },
];

const OutputBlock = ({ text, testid }) => {
  const [copied, setCopied] = useState(false);
  const copy = () => navigator.clipboard.writeText(text).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1500); toast.success("Copied"); });
  return (
    <div className="rounded-2xl border border-warm-200 bg-white p-5" data-testid={testid}>
      <div className="flex items-center justify-between mb-2">
        <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-saffron-700">Output</div>
        <button onClick={copy} className="inline-flex items-center gap-1 rounded-full border border-warm-200 h-7 px-2.5 text-[11px] font-semibold text-navy-700 hover:border-saffron-300 hover:text-saffron-700">
          {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />} Copy
        </button>
      </div>
      <div className="prose prose-sm max-w-none prose-headings:font-display prose-headings:text-navy-800 prose-p:my-2 whitespace-pre-wrap">
        <ReactMarkdown remarkPlugins={[remarkGfm]}>{text}</ReactMarkdown>
      </div>
    </div>
  );
};

const MarketingAssistant = () => {
  const { user, loading: authLoading, requireAuth } = useAuth();
  const [channel, setChannel] = useState("linkedin");
  const [campaigns, setCampaigns] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [audience, setAudience] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => { if (!authLoading && !user) requireAuth("Sign in to use the marketing assistant."); }, [user, authLoading, requireAuth]);
  useEffect(() => { if (user) fetchCampaigns({ mine: true, limit: 25 }).then((r) => { const items = r.items || r || []; setCampaigns(items); if (items.length) setSelectedId(items[0].id); }).catch(() => {}); }, [user]);

  const selected = campaigns.find((c) => c.id === selectedId);
  const run = async () => {
    if (!selected) { toast.error("Pick a campaign first"); return; }
    setLoading(true); setResult(null);
    try {
      const snap = { title: selected.title, tagline: selected.tagline, story: selected.story, goal: selected.goal, category: selected.category, rewards: selected.rewards || [] };
      const r = await aiMarketing({ channel, campaign: snap, audience });
      setResult(r.data);
    } catch (e) { toast.error(e.message || "Failed"); } finally { setLoading(false); }
  };

  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-10" data-testid="marketing-assistant-page">
      <div>
        <div className="flex items-center gap-2 text-saffron-700"><Sparkles className="h-4 w-4" /><span className="text-xs font-semibold uppercase tracking-[0.2em]">Marketing Assistant</span></div>
        <h1 className="mt-1 font-display text-3xl md:text-4xl font-bold tracking-tight text-navy-800">Ready-to-post copy for every channel.</h1>
        <p className="mt-2 text-navy-600 max-w-2xl">Pick a channel — get platform-tuned copy, hashtags, and posting tips.</p>
      </div>

      <div className="mt-6 rounded-2xl border border-warm-200 bg-white p-4 flex flex-wrap gap-2">
        {CHANNELS.map((c) => {
          const Icon = c.icon;
          const active = channel === c.id;
          return (
            <button key={c.id} onClick={() => { setChannel(c.id); setResult(null); }} data-testid={`ma-channel-${c.id}`} className={`inline-flex items-center gap-1.5 rounded-full px-3 h-9 text-sm font-semibold transition-colors ${active ? "bg-navy-800 text-white" : "text-navy-600 hover:text-navy-800 hover:bg-warm-100"}`}>
              <Icon className="h-4 w-4" /> {c.label}
            </button>
          );
        })}
      </div>

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-4 rounded-2xl border border-warm-200 bg-white p-6 space-y-4">
          <div>
            <label className="text-xs font-semibold text-navy-700 uppercase tracking-wide">Campaign</label>
            <select value={selectedId} onChange={(e) => setSelectedId(e.target.value)} data-testid="ma-campaign" className="mt-1.5 w-full h-11 rounded-2xl border border-warm-200 bg-white px-3 text-sm focus:outline-none focus:border-saffron-500 focus:ring-4 focus:ring-saffron-500/15 transition">
              {campaigns.map((c) => <option key={c.id} value={c.id}>{c.title || "(untitled)"}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-semibold text-navy-700 uppercase tracking-wide">Audience (optional)</label>
            <input value={audience} onChange={(e) => setAudience(e.target.value)} placeholder="e.g. hardware builders, wellness enthusiasts" className="mt-1.5 w-full h-11 rounded-2xl border border-warm-200 bg-white px-3 text-sm focus:outline-none focus:border-saffron-500 focus:ring-4 focus:ring-saffron-500/15 transition" />
          </div>
          <button onClick={run} disabled={loading || !selected} data-testid="ma-run" className="btn-saffron w-full inline-flex items-center justify-center gap-2 rounded-2xl h-11 font-semibold disabled:opacity-60">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
            {loading ? "Writing…" : `Generate ${CHANNELS.find((c) => c.id === channel)?.label}`}
          </button>
        </div>

        <div className="lg:col-span-8 space-y-4">
          {!result && loading && (<div className="rounded-2xl border border-warm-200 bg-white p-12 text-center"><Loader2 className="h-8 w-8 mx-auto animate-spin text-saffron-600" /><p className="mt-3 text-navy-500">Crafting your post…</p></div>)}
          {!result && !loading && (<div className="rounded-2xl border border-warm-200 bg-cream-100 p-10 text-center text-sm text-navy-600">Pick a channel and click <span className="font-semibold text-saffron-700">Generate</span>.</div>)}
          {result && (
            <>
              {result.primary && <OutputBlock text={result.primary} testid="ma-primary" />}
              {result.variants && result.variants.map((v, i) => <OutputBlock key={i} text={v} testid={`ma-variant-${i}`} />)}
              {result.tagline_and_comment && (
                <>
                  <OutputBlock text={`**Tagline:** ${result.tagline_and_comment.tagline}`} testid="ma-ph-tagline" />
                  <OutputBlock text={result.tagline_and_comment.first_comment} testid="ma-ph-comment" />
                </>
              )}
              {result.email && (
                <>
                  <OutputBlock text={`**Subject:** ${result.email.subject}\n\n**Preheader:** ${result.email.preheader}`} testid="ma-email-subject" />
                  <OutputBlock text={result.email.body} testid="ma-email-body" />
                </>
              )}
              {result.checklist && (
                <div className="rounded-2xl border border-warm-200 bg-white p-5" data-testid="ma-checklist">
                  <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-saffron-700 mb-3">Launch checklist</div>
                  <div className="space-y-2">
                    {result.checklist.map((it, i) => (
                      <div key={i} className="flex items-start gap-3 rounded-xl bg-cream-100 px-3 py-2 text-sm">
                        <span className="text-[10px] font-bold uppercase tracking-wider text-saffron-700 min-w-[46px] mt-0.5">{it.day}</span>
                        <div className="flex-1"><div className="text-navy-800">{it.task}</div>{it.category && <div className="text-[10px] uppercase tracking-wider text-navy-500 mt-0.5">{it.category}</div>}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {result.hashtags?.length > 0 && (
                <div className="rounded-2xl border border-warm-200 bg-white p-4">
                  <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-saffron-700 mb-2">Suggested hashtags</div>
                  <div className="flex flex-wrap gap-1.5">{result.hashtags.map((h, i) => <span key={i} className="text-xs font-semibold rounded-full bg-cream-100 border border-warm-200 px-2.5 py-1 text-navy-700">{h}</span>)}</div>
                </div>
              )}
              {result.usage_tips?.length > 0 && (
                <div className="rounded-2xl border border-warm-200 bg-white p-4">
                  <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-saffron-700 mb-2">Posting tips</div>
                  <ul className="space-y-1 text-sm text-navy-700">{result.usage_tips.map((t, i) => <li key={i} className="flex gap-1.5"><span className="text-saffron-600">•</span>{t}</li>)}</ul>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default MarketingAssistant;
